import {LinkText} from '@/components/atoms';
import PeacockDataTable from '@/components/organisms/PeacockDataTable';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {formatDateToLocalYYYYMMDD} from '@/utility/dateHelper';
import {
  API_STATUS,
  DISABLE_HEADERS,
  STATUS_VALUES
} from '@/utils/constants/commonConstants';
import {useSalesTargets} from '@/hooks/useSalesTargets';
import {getSalesTargetsReporteDownload} from '@/services/salesTargets';
import {useCallback, useMemo, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  DROP_SIZE_TYPES,
  EARLY_PAYMENT_TYPES
} from '@/utils/constants/saleTargetConstants';

const getReportLabel = (type) => {
  if (EARLY_PAYMENT_TYPES.includes(type)) return 'Early Payment Report';
  if (DROP_SIZE_TYPES.includes(type)) return 'Drop Size Report';
  return 'Sales Target Report';
};

const onDownloadForSalesTargetsReport = async (selectedIds, status, type) => {
  if (status?.toLowerCase() === API_STATUS.DRAFT?.toLowerCase()) {
    return;
  }
  const data = await getSalesTargetsReporteDownload(
    `salesTargetId=${selectedIds}`
  );
  const dateStr = getDateWithTimeString();
  let fileName = `SalesTarget_Report${dateStr}`;
  if (EARLY_PAYMENT_TYPES.includes(type)) {
    fileName = `EarlyPayment_Invoice_Report${dateStr}`;
  } else if (DROP_SIZE_TYPES.includes(type)) {
    fileName = `DropSizePoint_Invoice_Report${dateStr}`;
  }
  downloadCSVFiles(data, fileName);
};

// Kept for backward compatibility (used by SegmentsModal)
// eslint-disable-next-line
export const cellDataMapperObj = (navigate) => ({
  salesTargetName: {
    cellData: ({row: {original} = {}}) => (
      <LinkText
        text={original?.salesTargetName || '\u2014'}
        id={original?.salesTargetName + ' ' + (original?.id || original?.uuid)}
        handleClick={() =>
          navigate(
            `${ROUTES.SALES_TARGET_DETAIL.replace(':id', encodeURIComponent(original?.id || original?.uuid))}?status=${encodeURIComponent(original?.status)}`
          )
        }
      />
    ),
    linkHandler: (row) =>
      navigate(
        `${ROUTES.SALES_TARGET_DETAIL.replace(':id', encodeURIComponent(row?.id || row?.uuid))}?status=${encodeURIComponent(row?.status)}`
      ),
    linkLabel: (row) => row.salesTargetName || '\u2014'
  },
  numEligibleStores: {
    cellData: ({row: {original} = {}}) => (
      <div>{original?.numEligibleStores ?? '\u2014'}</div>
    )
  },
  percentTargetAchieved: {
    cellData: ({row: {original} = {}}) => {
      if (
        EARLY_PAYMENT_TYPES.includes(original?.type) ||
        DROP_SIZE_TYPES.includes(original?.type)
      ) {
        return <div>{'\u2014'}</div>;
      }
      return <div>{original?.percentTargetAchieved ?? '\u2014'}</div>;
    }
  },
  pointsAwarded: {
    cellData: ({row: {original} = {}}) => {
      if (
        EARLY_PAYMENT_TYPES.includes(original?.type) ||
        DROP_SIZE_TYPES.includes(original?.type)
      ) {
        return <div>{'\u2014'}</div>;
      }
      return <div>{original?.pointsAwarded ?? '\u2014'}</div>;
    }
  },
  startDate: {
    cellData: ({row: {original} = {}}) => (
      <div>
        {formatDateToLocalYYYYMMDD(
          original?.createdAt ?? original?.startDate
        ) || '\u2014'}
      </div>
    )
  },
  endDate: {
    cellData: ({row: {original} = {}}) => (
      <div>{formatDateToLocalYYYYMMDD(original?.endDate) || '\u2014'}</div>
    )
  },
  status: {
    cellData: ({row: {original} = {}}) => (
      <div
        className={
          original?.status === STATUS_VALUES.Error ? 'status_error' : ''
        }>
        {original?.status ?? '\u2014'}
      </div>
    )
  },
  reportUrl: {
    cellData: ({row: {original} = {}}) => {
      const reportLabel = getReportLabel(original?.type);
      if (original.status?.toLowerCase() === API_STATUS.DRAFT?.toLowerCase()) {
        return <div className='text-gray-500'>{reportLabel}</div>;
      }
      return (
        <LinkText
          text={reportLabel}
          id={original?.id}
          isPaddingHidden={true}
          handleClick={() =>
            onDownloadForSalesTargetsReport(
              original.id,
              original.status,
              original.type
            )
          }
        />
      );
    }
  },
  lastUpdatedDate: {
    cellData: ({row: {original} = {}}) => (
      <div>
        {formatDateToLocalYYYYMMDD(
          original?.lastUpdated ?? original?.lastUpdatedDate
        ) || '\u2014'}
      </div>
    )
  },
  updatedBy: {
    cellData: ({row: {original} = {}}) => (
      <div>{original?.updatedBy ?? '\u2014'}</div>
    )
  }
});

export const SalesTargetList = () => {
  const navigate = useNavigate();
  const {
    salesTargetsData,
    salesTargetsColConfigData,
    totalRecords,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    setSortingObj,
    sortingObj,
    setSearchArry,
    onDownloadBtnClick,
    isLoading,
    isFetching,
    handleResetAll
  } = useSalesTargets();

  // Map cell renderers onto the backend column config
  const columns = useMemo(
    () =>
      getCellDataMappedCols(
        cellDataMapperObj(navigate),
        salesTargetsColConfigData
      ),
    [navigate, salesTargetsColConfigData]
  );

  const handleDownload = useCallback(
    (rows, {isAllSelected = false, excludeIds = []} = {}) => {
      const ids = isAllSelected
        ? []
        : rows.map((r) => r.original?.id || r.original?.uuid || r.id || r.uuid);
      onDownloadBtnClick(ids, excludeIds);
    },
    [onDownloadBtnClick]
  );

  // Stable records reference — prevents new [] on every render during loading
  const emptyRecords = useRef([]);
  const records = salesTargetsData?.data ?? emptyRecords.current;

  // Memoized config objects — inline objects create new references every render,
  // causing the Peacock DataTable's internal useEffects to fire unnecessarily.
  const onRowsPerPageChange = useCallback(
    (newPageSize) => {
      setPageSize(newPageSize);
      setPageIndex(0);
    },
    [setPageSize, setPageIndex]
  );

  const paginationConfig = useMemo(
    () => ({pageIndex, setPageIndex, onRowsPerPageChange}),
    [pageIndex, setPageIndex, onRowsPerPageChange]
  );

  const sortingConfig = useMemo(
    () => ({handleSorting: setSortingObj, initialSortConfig: sortingObj}),
    [setSortingObj, sortingObj]
  );

  const filterConfig = useMemo(
    () => ({
      setSearchArry,
      onResetAll: handleResetAll,
      showResetAll: true,
      resetAllPosition: 'left',
      disabledHeaders: DISABLE_HEADERS.SALES_TARGET_HEADERS
    }),
    [setSearchArry, handleResetAll]
  );

  // Header-checkbox select-all spans all pages (legacy UX).
  const selectionConfig = useMemo(() => ({enableSelectAllPages: true}), []);

  const toolbarConfig = useMemo(
    () => ({showDownload: true, onDownload: handleDownload}),
    [handleDownload]
  );

  return (
    <div className='salesTargetsDataTable'>
      <PeacockDataTable
        columns={columns}
        records={records}
        isLoading={isLoading}
        isFetching={isFetching}
        pageSize={pageSize}
        totalRecords={totalRecords}
        paginationConfig={paginationConfig}
        selectionConfig={selectionConfig}
        sortingConfig={sortingConfig}
        filterConfig={filterConfig}
        toolbarConfig={toolbarConfig}
      />
      {totalRecords > 0 && (
        <div className='text-md py-2 pr-4 text-right text-[color:var(--color-text-primary)]'>
          {totalRecords} total items
        </div>
      )}
    </div>
  );
};
